@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    --font-heading: 'Archivo', 'Helvetica Neue', Arial, sans-serif;
    --font-body: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
    --font-label: 'IBM Plex Mono', ui-monospace, monospace;

    /* background — warm paper / machine housing */
    --background-50: 0.991 0.003 85;
    --background-100: 0.975 0.005 84;
    --background-200: 0.946 0.007 82;
    --background-300: 0.902 0.009 80;
    --background-400: 0.822 0.01 78;
    --background-500: 0.72 0.01 75;
    --background-600: 0.6 0.01 72;
    --background-700: 0.47 0.009 70;
    --background-800: 0.34 0.008 68;
    --background-900: 0.24 0.007 66;
    --background-950: 0.16 0.006 64;

    /* primary — precision red (brand) */
    --primary-50: 0.968 0.012 25;
    --primary-100: 0.94 0.031 25;
    --primary-200: 0.881 0.061 25;
    --primary-300: 0.79 0.108 26;
    --primary-400: 0.684 0.168 27;
    --primary-500: 0.578 0.212 28;
    --primary-600: 0.503 0.201 28;
    --primary-700: 0.424 0.171 28;
    --primary-800: 0.35 0.132 28;
    --primary-900: 0.283 0.101 28;
    --primary-950: 0.2 0.068 28;

    /* accent — amber / copper, technical highlights */
    --accent-50: 0.979 0.016 82;
    --accent-100: 0.951 0.041 81;
    --accent-200: 0.901 0.081 79;
    --accent-300: 0.842 0.121 77;
    --accent-400: 0.782 0.151 73;
    --accent-500: 0.72 0.161 68;
    --accent-600: 0.628 0.149 62;
    --accent-700: 0.519 0.129 55;
    --accent-800: 0.421 0.101 50;
    --accent-900: 0.331 0.078 46;
    --accent-950: 0.232 0.052 44;

    /* secondary — steel / graphite support */
    --secondary-50: 0.978 0.003 250;
    --secondary-100: 0.951 0.005 250;
    --secondary-200: 0.89 0.008 250;
    --secondary-300: 0.802 0.011 250;
    --secondary-400: 0.681 0.013 251;
    --secondary-500: 0.559 0.014 251;
    --secondary-600: 0.46 0.013 252;
    --secondary-700: 0.371 0.012 254;
    --secondary-800: 0.281 0.01 256;
    --secondary-900: 0.208 0.008 256;
    --secondary-950: 0.14 0.006 258;

    /* foreground — ink */
    --foreground-50: 0.971 0.003 60;
    --foreground-100: 0.931 0.004 60;
    --foreground-200: 0.861 0.005 60;
    --foreground-300: 0.752 0.006 60;
    --foreground-400: 0.621 0.007 60;
    --foreground-500: 0.503 0.008 60;
    --foreground-600: 0.402 0.008 62;
    --foreground-700: 0.311 0.008 62;
    --foreground-800: 0.231 0.007 62;
    --foreground-900: 0.171 0.006 62;
    --foreground-950: 0.109 0.005 62;
  }

  .dark {
    --background-50: 0.16 0.006 64;
    --background-100: 0.2 0.007 65;
    --background-200: 0.26 0.008 66;
    --background-300: 0.34 0.008 68;
    --background-400: 0.45 0.008 70;
    --background-500: 0.58 0.008 72;
    --background-600: 0.7 0.008 76;
    --background-700: 0.81 0.007 78;
    --background-800: 0.9 0.006 80;
    --background-900: 0.95 0.004 84;
    --background-950: 0.985 0.002 85;

    --foreground-50: 0.11 0.005 62;
    --foreground-100: 0.17 0.006 62;
    --foreground-200: 0.231 0.007 62;
    --foreground-300: 0.311 0.008 62;
    --foreground-400: 0.402 0.008 62;
    --foreground-500: 0.503 0.008 60;
    --foreground-600: 0.621 0.007 60;
    --foreground-700: 0.752 0.006 60;
    --foreground-800: 0.861 0.005 60;
    --foreground-900: 0.931 0.004 60;
    --foreground-950: 0.971 0.003 60;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-body);
    background-color: oklch(var(--background-50));
    color: oklch(var(--foreground-900));
    -webkit-font-smoothing: antialiased;
  }

  h1,
  h2,
  h3,
  h4 {
    font-family: var(--font-heading);
    letter-spacing: -0.02em;
  }

  ::selection {
    background-color: oklch(var(--primary-500));
    color: oklch(var(--background-50));
  }

  section[id] {
    scroll-margin-top: 96px;
  }
}

@layer components {
  .hairline {
    background-image: linear-gradient(
      to right,
      oklch(var(--foreground-950) / 0.12) 0 1px,
      transparent 1px 100%
    );
    background-size: 8px 100%;
  }

  .blueprint {
    background-image:
      linear-gradient(to right, oklch(var(--foreground-50) / 0.06) 0 1px, transparent 1px 100%),
      linear-gradient(to bottom, oklch(var(--foreground-50) / 0.06) 0 1px, transparent 1px 100%);
    background-size: 48px 48px;
  }

  .grid-paper {
    background-image:
      linear-gradient(to right, oklch(var(--foreground-950) / 0.06) 0 1px, transparent 1px 100%),
      linear-gradient(to bottom, oklch(var(--foreground-950) / 0.06) 0 1px, transparent 1px 100%);
    background-size: 32px 32px;
  }

  .hp-field {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
  }
}